import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { RadioTower, ShieldCheck, Lock, Info } from 'lucide-react';
import { ConnectCard, TWITCH_SCOPES, type PlatformConnectionView } from './components/ConnectCard.js';
import { DisconnectAlertDialog } from './components/DisconnectAlertDialog.js';
import { useMe, useRevokeSelf } from './hooks.js';
import { oauthErrorMessage } from './oauth-error-messages.js';

export function ConnectPage() {
  const me = useMe();
  const revoke = useRevokeSelf();
  const [searchParams, setSearchParams] = useSearchParams();
  const errorCode = searchParams.get('error');
  // Plataforma alvo do AlertDialog de disconnect (null = fechado).
  const [pendingDisconnect, setPendingDisconnect] = useState<'twitch' | 'youtube' | null>(null);

  // Toast estruturado de erro OAuth + limpar querystring pra não disparar de novo
  // em rerender (roteiro Fase 1 §C.7). `id` estável dedupe em StrictMode dev
  // (effect roda 2x) e em rerender pré-limpeza do param.
  useEffect(() => {
    if (!errorCode) return;
    toast.error(oauthErrorMessage(errorCode), { id: 'oauth-callback-error' });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('error');
        return next;
      },
      { replace: true },
    );
  }, [errorCode, setSearchParams]);

  // C.1 — se a sessão já existe (callback OAuth bem-sucedido voltou pra cá),
  // empurra direto pro painel. SPA navigation, sem window.location.
  //
  // Gate `!errorCode`: cenário "user logado tenta anexar YT e callback falha"
  // chega com sessão Twitch válida + ?error=…. Sem o gate, o Navigate dispara
  // antes do useEffect commitar e o toast ou se perde ou aparece sobre /painel
  // sem contexto. Com o gate, renderiza ConnectPage → toast dispara + limpa o
  // param → próximo render (errorCode=null) faz o Navigate.
  if (me.isSuccess && me.data && !errorCode) {
    return <Navigate to="/painel" replace />;
  }

  const twitchConn = me.data?.connections?.twitch;
  const youtubeConn = me.data?.connections?.youtube;
  const connections: PlatformConnectionView[] = [
    {
      platformKey: 'twitch',
      name: 'Twitch',
      connected: Boolean(twitchConn),
      ...(twitchConn ? { connectedAs: `@${twitchConn.displayName}` } : {}),
      scopes: TWITCH_SCOPES,
    },
    {
      platformKey: 'youtube',
      name: 'YouTube',
      connected: Boolean(youtubeConn),
      ...(youtubeConn ? { connectedAs: youtubeConn.displayName } : {}),
      scopes: ['youtube.force-ssl (Live Chat: list/ban/delete/insert)'],
    },
  ];

  const handleConnect = (platform: 'twitch' | 'youtube') => {
    window.location.href = `/auth/${platform}?return_to=/auth/connect`;
  };

  // Abre AlertDialog destructive (#100). Twitch only no MVP — disconnect de
  // YouTube ainda não implementado no backend (only revoke wholesale).
  const handleDisconnect = (platform: 'twitch' | 'youtube') => {
    if (platform !== 'twitch') return;
    setPendingDisconnect(platform);
  };

  const channelLabelForDialog =
    pendingDisconnect === 'twitch' && twitchConn ? `@${twitchConn.displayName}` : '';

  // #118: grid topbar / main / trust footer (paridade com screen-connect-1.html).
  // O brand sai do bloco centrado e vai pra topbar 56px; o trust strip migra do
  // card pro footer full-width. #113: brand usa RadioTower, igual à TopBar do painel.
  return (
    <div className="min-h-screen grid grid-rows-[56px_1fr_auto] bg-bg">
      <header className="flex items-center gap-2 px-6 border-b border-border bg-bg-elevated">
        <span
          aria-hidden="true"
          className="w-[22px] h-[22px] rounded-[5px] grid place-items-center text-primary-fg"
          style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(192 76% 42%))' }}
        >
          <RadioTower size={13} strokeWidth={2.5} />
        </span>
        <span className="font-semibold text-base tracking-tight">Multimod</span>
        {/* package.json é 0.0.0 (placeholder) — rótulo estático "MVP" em vez de
            inventar uma versão. */}
        <span className="ml-1 text-xs font-mono text-fg-subtle">MVP</span>
      </header>

      <main className="grid place-items-center px-4 py-12">
        <ConnectCard
          connections={connections}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
        />
      </main>

      <footer className="flex flex-wrap items-center justify-center gap-6 px-6 py-6 border-t border-border">
        <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
          <ShieldCheck size={13} strokeWidth={1.75} className="text-fg-subtle" />
          OAuth 2.0 oficial — nunca pedimos sua senha
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
          <Lock size={13} strokeWidth={1.75} className="text-fg-subtle" />
          Tokens cifrados, revogáveis a qualquer momento
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
          <Info size={13} strokeWidth={1.75} className="text-fg-subtle" />
          Você pode desconectar nas Configurações
        </span>
      </footer>

      <DisconnectAlertDialog
        open={pendingDisconnect !== null}
        channelLabel={channelLabelForDialog}
        onOpenChange={(o) => !o && setPendingDisconnect(null)}
        onConfirm={() => {
          revoke.mutate();
          setPendingDisconnect(null);
        }}
      />
    </div>
  );
}
